import { useEffect, useState } from 'react';
import { cn } from '@/lib/cn';

/**
 * The in-app "Install" affordance, and the reason there is one.
 *
 * Chromium fires `beforeinstallprompt` and, if nothing calls
 * `preventDefault()`, decides on its own whether to surface a mini-infobar —
 * which it increasingly does not. So an installable app with no handler is
 * installable only through a browser menu most people never open. For a
 * field-capture app that is the difference between an icon on the home screen
 * and a tab that gets closed.
 *
 * Capturing the event lets us ask at a moment that makes sense. It also has to
 * be captured to be used at all: the event is only valid until the handler
 * returns, so `prompt()` needs the stashed reference.
 *
 * Deliberately quiet:
 *   - Nothing renders unless the browser actually offered. Safari and iOS
 *     never fire this, and a permanently visible "Install" button that does
 *     nothing there is worse than no button.
 *   - A dismissal sticks, per browser, so this asks once rather than nagging
 *     someone mid-inspection.
 *   - `appinstalled` clears it, for the case where the user installed through
 *     the browser's own UI instead.
 */

/** Not in every lib.dom yet; narrow to what is actually called. */
interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISSED_KEY = 'kapture.installDismissed';

function dismissedAlready(): boolean {
  try {
    return localStorage.getItem(DISMISSED_KEY) === '1';
  } catch {
    // Private mode and blocked site-data both throw on access. Asking is a
    // better failure than crashing the shell that renders it.
    return false;
  }
}

export function InstallPrompt() {
  const [deferred, setDeferred] = useState<InstallPromptEvent | null>(null);
  const [hidden, setHidden] = useState(dismissedAlready);

  useEffect(() => {
    function onBeforeInstall(e: Event) {
      // Without this Chromium may show its own bar as well as ours.
      e.preventDefault();
      setDeferred(e as InstallPromptEvent);
    }
    function onInstalled() {
      setDeferred(null);
      setHidden(true);
    }
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  if (!deferred || hidden) return null;

  function dismiss() {
    setHidden(true);
    try {
      localStorage.setItem(DISMISSED_KEY, '1');
    } catch {
      // Session-only dismissal is an acceptable outcome; `hidden` already
      // covers this page's lifetime.
    }
  }

  async function install() {
    if (!deferred) return;
    await deferred.prompt();
    await deferred.userChoice;
    // Spent either way — the event cannot be prompted twice, so holding it
    // would leave a button that silently does nothing on a second tap.
    setDeferred(null);
  }

  return (
    <div
      className={cn(
        'border-brand/30 bg-brand-soft text-brand-ink mx-auto flex w-full max-w-[480px]',
        'items-center gap-3 border-b px-4 py-2 text-xs',
      )}
    >
      <div className="flex-1">
        <b className="font-semibold">Install Kapture</b> — works offline, opens like an app.
      </div>
      <button
        type="button"
        onClick={() => void install()}
        className="bg-brand text-brand-ink rounded-md px-3 py-1 text-[11px] font-semibold"
      >
        Install
      </button>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss install prompt"
        className="text-text-dim px-1 text-[11px]"
      >
        Not now
      </button>
    </div>
  );
}
